import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Typography, message } from 'antd'
import LoadingSpinner from '../../../components/loadingSpinner'
import { useQuery, useMutation } from '@apollo/react-hooks'
import type {
	PendingLicenseContractsQuery,
	LicenseContractsGroupType,
	LicenseContractType,
	AcceptAllLicenseAgreementsMutation,
	GeneralLicenseFacilityType,
	LicenseTextType
} from '../../../generated/types'
import PageLayout from '../../layout/containers/PageLayout'
import { ContractCard } from '../components/ContractCard'
import { GeneralFacilityRow } from '../components/GeneralFacilityRow'
import { PENDING_LICENSE_CONTRACTS } from '../../core/queries'
import { ACCEPT_ALL_LICENSE_AGREEMENTS } from '../../core/mutations'
import { ROUTES } from '../../navigation/routes'
import {
	StyledContent,
	StyledPageTitle,
	StyledSystemSection,
	StyledConfirmationText,
	StyledAcceptButton,
	StyledCenteredContainer,
	StyledEmptyDescription
} from './styles'
import UserContext from '../../../providers/userProvider/userContext'
import { StyledCard } from '../components/styles'
import { safeOpen, sanitizeHtml, escapeHtml } from '../../../utils/helper.utils'
import type { Previewable } from './types'
const { Title } = Typography

export const LicenseAgreementPage = () => {
	const { t } = useTranslation()
	const history = useHistory()
	const { user } = React.useContext(UserContext)

	// Use GraphQL query
	const { data, loading, error } = useQuery<PendingLicenseContractsQuery>(
		PENDING_LICENSE_CONTRACTS,
		{
			fetchPolicy: 'network-only'
		}
	)

	// Get pending agreements and validate data
	const rawPendingAgreements =
		data?.pendingLicenseContracts?.filter(
			(system): system is LicenseContractsGroupType => system !== null
		) || []

	// Filter and validate: only include systems with valid data
	// Create new objects to avoid mutating read-only GraphQL data
	const pendingAgreements = rawPendingAgreements
		.map(system => {
			// Validate system has required data
			if (!system.softwareSystem) {
				console.warn('System has no software system data, skipping:', system)
				return null
			}

			// Filter out contracts with incomplete data
			const validContracts =
				system.contracts
					?.filter(
						(contract): contract is LicenseContractType => contract !== null
					)
					?.filter(contract => {
						const isValid =
							contract.id &&
							contract.licenseVersion?.version &&
							contract.licenseVersion?.publishedAt &&
							contract.licenseVersion?.licenseMaster?.name

						if (!isValid) {
							console.warn('Invalid contract data detected:', {
								contractId: contract.id,
								version: contract.licenseVersion?.version,
								publishedAt: contract.licenseVersion?.publishedAt,
								licenseName: contract.licenseVersion?.licenseMaster?.name
							})
						}
						return isValid
					}) || []

			// Only include system if it has at least one valid contract
			if (validContracts.length === 0) {
				const systemIdentifier = system.softwareSystem?.systemName || 'Unknown'
				console.warn(
					'System has no valid contracts, skipping:',
					systemIdentifier
				)
				return null
			}

			// Return new object with only valid contracts (avoid mutating read-only data)
			// Use type assertion since we already validated contracts
			return {
				...system,
				contracts: validContracts as LicenseContractType[]
			} as LicenseContractsGroupType & {
				contracts: LicenseContractType[]
			}
		})
		.filter(
			(
				system
			): system is NonNullable<
				LicenseContractsGroupType & {
					contracts: LicenseContractType[]
				}
			> => system !== null
		)

	// General facilities are additional documents that are not tied to a
	// specific software system. They are handled separately from
	// `pendingAgreements` and can be previewed just like contract texts.
	const generalFacilities: GeneralLicenseFacilityType[] =
		data?.allGeneralLicenseFacilities?.filter(
			(f): f is GeneralLicenseFacilityType => f !== null
		) ?? []

	// Use GraphQL mutation
	const [acceptAgreements, { loading: acceptLoading }] =
		useMutation<AcceptAllLicenseAgreementsMutation>(
			ACCEPT_ALL_LICENSE_AGREEMENTS
		)

	// Handle loading state
	if (loading) {
		return (
			<PageLayout
				renderMainContent={
					<StyledContent>
						<StyledCenteredContainer>
							<LoadingSpinner />
						</StyledCenteredContainer>
					</StyledContent>
				}
			/>
		)
	}

	// Handle error state
	if (error) {
		message.error(t('license.agreements.error.loadFailed'))
		console.error('Error loading pending agreements:', error)
		return (
			<PageLayout
				renderMainContent={
					<StyledContent>
						<StyledCenteredContainer>
							<p>{t('license.agreements.error.loadFailed')}</p>
						</StyledCenteredContainer>
					</StyledContent>
				}
			/>
		)
	}

	// Handle empty state - no pending agreements
	if (!pendingAgreements || pendingAgreements.length === 0) {
		return (
			<PageLayout
				renderMainContent={
					<StyledContent>
						<StyledCenteredContainer>
							<Title level={3}>
								{t('license.agreements.noAgreements.title')}
							</Title>
							<StyledEmptyDescription>
								{String(t('license.agreements.noAgreements.description'))}
							</StyledEmptyDescription>
						</StyledCenteredContainer>
					</StyledContent>
				}
			/>
		)
	}

	const asPreviewableFromLicenseText = (t: LicenseTextType): Previewable => ({
		kind: 'licenseText',
		title: t.title,
		pdfUrl: t.pdfUrl,
		renderedText: t.renderedText
	})

	// Helper used by both agreement and facility preview logic. Accepts a
	// facility object and maps it to the generic `Previewable` union so
	// that `openPreview` can handle it uniformly with license texts.
	const asPreviewableFromGeneralFacility = (
		f: GeneralLicenseFacilityType
	): Previewable => ({
		kind: 'generalFacility',
		title: f.title,
		pdfUrl: f.pdfUrl,
		htmUrl: f.htmUrl
	})

	const openPreview = (item: Previewable) => {
		if (item.pdfUrl) {
			safeOpen(item.pdfUrl)
			return
		}

		if (item.kind === 'licenseText') {
			const title = escapeHtml(item.title || '')
			const renderedText = sanitizeHtml(item.renderedText || '')

			const htmlContent = `
				<!DOCTYPE html>
				<html lang="de">
				<head>
					<meta charset="UTF-8">
					<title>${title}</title>
					<style>
					body { padding: 40px; max-width: 900px; margin: 0 auto; }
					</style>
				</head>
				<body>
					<div>${renderedText}</div>
				</body>
				</html>
				`
			const blob = new Blob([htmlContent], { type: 'text/html' })
			const url = URL.createObjectURL(blob)
			safeOpen(url)
			setTimeout(() => URL.revokeObjectURL(url), 1000)
			return
		}

		if (item.kind === 'generalFacility' && item.htmUrl) {
			safeOpen(item.htmUrl)
			return
		}
	}

	const handlePreviewClick = (text: LicenseTextType) => {
		openPreview(asPreviewableFromLicenseText(text))
	}

	const handleAccept = async () => {
		try {
			const { data: mutationData } = await acceptAgreements()
			if (mutationData?.acceptAllLicenseAgreements?.success) {
				// Show success message if provided by backend
				if (mutationData.acceptAllLicenseAgreements.message) {
					message.success(mutationData.acceptAllLicenseAgreements.message)
				}
				// Update user context to reflect that agreements are accepted
				user.hasPendingLicenseAgreements = false
				// Redirect to home page
				history.push(ROUTES.home)
			} else {
				// Show error message from backend or generic message
				const errorMsg =
					mutationData?.acceptAllLicenseAgreements?.message ||
					t('license.agreements.error.acceptFailed')
				message.error(errorMsg)
			}
		} catch (error) {
			console.error('Error accepting agreements:', error)
			message.error(t('license.agreements.error.acceptFailed'))
		}
	}

	const mainContent = (
		<StyledContent>
			<StyledPageTitle level={2}>
				{t('license.agreements.title')}
			</StyledPageTitle>

			{pendingAgreements.map(system => {
				// Ensure we have a valid system name, fallback to avoid showing placeholders
				const systemName =
					system.softwareSystem?.systemName || 'Unbekanntes System'

				return (
					<StyledSystemSection key={system.softwareSystem?.id}>
						<Title level={3}>
							{t('license.agreements.system.title', {
								systemName: systemName
							})}
						</Title>

						{(system.contracts as LicenseContractType[]).map(contract => (
							<ContractCard
								key={contract.id}
								contract={contract}
								onPreviewClick={handlePreviewClick}
							/>
						))}
					</StyledSystemSection>
				)
			})}

			{/* Render general facilities if any exist. Facilities are
							// sorted by sortOrder for consistent ordering. */}
			{generalFacilities.length > 0 && (
				<StyledSystemSection key="general-facilities">
					<Title level={3}>{t('license.contracts.generalFacilities')}</Title>

					<StyledCard>
						{generalFacilities
							.slice()
							.sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
							.map(f => (
								<GeneralFacilityRow
									key={f.id}
									facility={f}
									onPreview={facility =>
										openPreview(asPreviewableFromGeneralFacility(facility))
									}
								/>
							))}
					</StyledCard>
				</StyledSystemSection>
			)}

			<StyledConfirmationText>
				{t('license.agreements.confirmation')}
			</StyledConfirmationText>

			<StyledAcceptButton
				type="primary"
				size="large"
				onClick={handleAccept}
				loading={acceptLoading}
				disabled={acceptLoading}
			>
				{acceptLoading
					? t('license.agreements.accepting')
					: t('license.agreements.acceptButton')}
			</StyledAcceptButton>
		</StyledContent>
	)

	return <PageLayout renderMainContent={mainContent} />
}
